import { useEffect, useState } from 'react';
import { Button, Spinner, StatusBadge } from '@immo/ui-kit';
import type { FleetLaneCommandVerb, FleetMachineOverview } from '@immo/api-contract';
import { apiClient } from '@/lib/auth/api-client';
import { useTranslations } from '@/i18n/use-translations';
import { XIcon } from '@/components/layout/icons';
import { formatDuration } from '@/lib/landiq/format-duration';
import { FleetStateBadge } from '@/components/fleet/fleet-badges';
import { laneWorkers, type LaneWorker, type LaneWorkerStatus } from '@/lib/fleet/lane-workers';
import { useCommandVerbCopy } from '@/components/fleet/command-verbs';
import { isStoppedByOperator } from '@/components/fleet/profile-commands';

type Props = {
  readonly lane: string;
  /** Changes on every board poll: the machines are read again with it. */
  readonly refreshKey: unknown;
  /** `backoffice:fleet:command`: each row offers Start, Stop and Restart. Absent, the panel only reads. */
  readonly canCommand: boolean;
  readonly onClose: () => void;
};

/** How long after a sent verb the panel reads the machines again — about one host tick. */
const RE_READ_AFTER_MS = 5_000;

type Sent =
  | { readonly kind: 'idle' }
  | { readonly kind: 'sending'; readonly verb: FleetLaneCommandVerb }
  | { readonly kind: 'sent'; readonly verb: FleetLaneCommandVerb }
  | { readonly kind: 'failed'; readonly message: string };

function errorCodeOf(body: unknown): string | undefined {
  return (body as { error?: { code?: string } } | undefined)?.error?.code;
}

function VerbButton({
  verb,
  title,
  disabled,
  onClick,
}: {
  readonly verb: FleetLaneCommandVerb;
  readonly title?: string;
  readonly disabled: boolean;
  readonly onClick: () => void;
}) {
  const copy = useCommandVerbCopy(verb);
  const Icon = copy.icon;

  return (
    <Button type="button" size="sm" variant={copy.variant} title={title} disabled={disabled} onClick={onClick}>
      <Icon className="h-4 w-4" aria-hidden />
      {copy.label}
    </Button>
  );
}

/**
 * One worker's lane verbs, by the Fleet page's rule (fleet-agent ADR 0013): a lane that runs offers Stop, a lane the
 * operator stopped offers Start, every lane offers Restart. One click, no confirm — so Stop on a lane mid-round lets
 * that round finish first (`finish`), never cuts it at a step boundary. The verb goes to the host and is collected on
 * its next tick; the panel reads the machines again a few seconds on.
 */
function WorkerActions({
  lane,
  worker,
  onSent,
}: {
  readonly lane: string;
  readonly worker: LaneWorker;
  readonly onSent: () => void;
}) {
  const t = useTranslations('pages.admin.fleet-board.workers');
  const tErrors = useTranslations('errors');
  const [sent, setSent] = useState<Sent>({ kind: 'idle' });
  const running = worker.profile.state !== 'stopped';
  const busy = sent.kind === 'sending';

  const send = async (verb: FleetLaneCommandVerb) => {
    setSent({ kind: 'sending', verb });

    const body =
      verb === 'stop'
        ? { verb, profile: lane, ...(worker.profile.state === 'working' ? { finish: true } : {}) }
        : { verb, profile: lane };

    try {
      const result = await apiClient.adminFleetCommands.issue({ pathParams: { machineId: worker.machineId }, body });

      if (!result.ok) {
        const code = errorCodeOf(result.body);

        setSent({ kind: 'failed', message: code === undefined ? t('verb-failed') : tErrors(code) });

        return;
      }

      setSent({ kind: 'sent', verb });
      onSent();
    } catch {
      setSent({ kind: 'failed', message: t('verb-failed') });
    }
  };

  return (
    <div className="flex flex-col gap-1">
      <div className="flex flex-wrap items-center gap-2">
        {running && (
          <VerbButton
            verb="stop"
            disabled={busy}
            {...(worker.profile.state === 'working' ? { title: t('stop-after-round') } : {})}
            onClick={() => void send('stop')}
          />
        )}
        {isStoppedByOperator(worker.profile) && (
          <VerbButton verb="start" disabled={busy} onClick={() => void send('start')} />
        )}
        <VerbButton verb="restart" disabled={busy} onClick={() => void send('restart')} />
        {busy && <Spinner size="sm" aria-hidden />}
      </div>
      {sent.kind === 'sent' && (
        <p role="status" className="text-xs text-text-secondary">
          {t('verb-sent', { machine: worker.machine })}
        </p>
      )}
      {sent.kind === 'failed' && (
        <p role="alert" className="text-xs text-danger-700">
          {sent.message}
        </p>
      )}
    </div>
  );
}

type Read = { readonly machines: ReadonlyArray<FleetMachineOverview> | null; readonly failed: boolean };

function StatusOf({ status }: { readonly status: LaneWorkerStatus }) {
  const t = useTranslations('pages.admin.fleet-board.workers');

  if (status === 'out-of-service') return <StatusBadge label={t('out-of-service')} tone="danger" dot />;

  if (status === 'offline') return <StatusBadge label={t('offline')} tone="neutral" />;

  return <FleetStateBadge state={status} />;
}

/**
 * Every worker of one lane across the fleet, laid over the right of the board like the ticket panel: which machine,
 * what it is doing — running, idle, waiting, out of service, stopped, offline — and when it last said so. Read from
 * the machines overview, the same reports the Fleet page shows.
 */
export function WorkersPanel({ lane, refreshKey, canCommand, onClose }: Props) {
  const t = useTranslations('pages.admin.fleet-board.workers');
  const [read, setRead] = useState<Read>({ machines: null, failed: false });
  // Bumped a few seconds after a verb is sent: the machines are read again, so the row shows what the host made of it.
  const [reloads, setReloads] = useState(0);
  const reReadSoon = () => setTimeout(() => setReloads((count) => count + 1), RE_READ_AFTER_MS);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !event.defaultPrevented) onClose();
    };

    document.addEventListener('keydown', onKey);

    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  // The last answer stays while a re-read is in flight.
  useEffect(() => {
    let cancelled = false;

    void apiClient.adminFleet.overview().then(
      (result) => {
        if (!cancelled)
          setRead((previous) => ({
            machines: result.ok ? result.body.data : previous.machines,
            failed: !result.ok,
          }));
      },
      () => {
        if (!cancelled) setRead((previous) => ({ ...previous, failed: true }));
      },
    );

    return () => {
      cancelled = true;
    };
  }, [refreshKey, reloads]);

  const workers = read.machines === null ? null : laneWorkers(read.machines, lane);
  const now = Date.now();

  return (
    <aside
      aria-label={t('title', { lane })}
      className="fixed inset-y-0 right-0 z-30 flex w-full max-w-[480px] flex-col border-l border-border bg-surface shadow-xl"
    >
      <div className="flex shrink-0 items-start gap-2 border-b border-neutral-100 px-6 py-4">
        <div className="flex min-w-0 flex-1 flex-col gap-1">
          <h2 className="text-lg font-semibold text-text-primary">{t('title', { lane })}</h2>
          {workers !== null && <p className="text-sm text-text-secondary">{t('count', { count: workers.length })}</p>}
        </div>
        <button
          type="button"
          onClick={onClose}
          aria-label={t('close')}
          title={t('close')}
          className="flex h-7 w-7 shrink-0 items-center justify-center rounded-sm text-text-tertiary hover:bg-hover-bg"
        >
          <XIcon className="h-4 w-4" aria-hidden />
        </button>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto px-6 py-5">
        {read.failed && <p className="mb-3 text-sm text-danger-700">{t('failed')}</p>}
        {workers === null ? (
          !read.failed && (
            <div className="flex justify-center py-8">
              <Spinner />
            </div>
          )
        ) : workers.length === 0 ? (
          <p className="text-sm text-text-secondary">{t('none', { lane })}</p>
        ) : (
          <ul className="flex flex-col divide-y divide-neutral-100">
            {workers.map((worker) => (
              <li key={`${worker.machine}/${worker.worker ?? ''}`} className="flex flex-col gap-1 py-3">
                <div className="flex items-center justify-between gap-3">
                  <span className="min-w-0 truncate font-medium text-text-primary">{worker.machine}</span>
                  <StatusOf status={worker.status} />
                </div>
                {worker.title !== null && (
                  <p className="break-words text-sm text-text-secondary" title={worker.title}>
                    {worker.title}
                  </p>
                )}
                <p className="text-xs text-text-tertiary">
                  {[worker.worker, t('reported', { duration: formatDuration(now - Date.parse(worker.reportedAt)) })]
                    .filter((part) => part !== null)
                    .join(' · ')}
                </p>
                {canCommand && worker.status !== 'offline' && (
                  <WorkerActions lane={lane} worker={worker} onSent={reReadSoon} />
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
    </aside>
  );
}
